import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { createHousehold, updateHouseholdById } from 'api/households';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import { useGivebutterForm } from 'components/form';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { usePossibleCurrentContactHousehold } from 'customHooks/useCurrentContactHousehold';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ContactHouseholdFormValues {
    name: string;
    envelope_name: string;
}

const ContactHouseholdFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const { accountId } = useCurrentAccount();
    const { data: household } = usePossibleCurrentContactHousehold();
    const isEdit = !!household;

    const methods = useGivebutterForm<ContactHouseholdFormValues>({
        defaultValues: {
            name: household?.name ?? '',
            envelope_name: household?.envelope_name ?? '',
        },
    });
    const { handleSubmit, resetFormButton, control, path } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (formValues: ContactHouseholdFormValues) => {
            const sharedProps = {
                name: formValues.name,
                envelopeName: formValues.envelope_name,
            };

            return isEdit
                ? updateHouseholdById(accountId, household.id, sharedProps)
                : createHousehold(accountId, { ...sharedProps, primaryContactId: contactId });
        },
        onSuccess: async () => {
            toast.success(`${isEdit ? 'Updated' : 'Created'} household!`);
            await queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
            });
            if (isEdit) {
                await queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.householdById(accountId, household.id),
                });
            }

            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(
                error.response?.data.message ??
                    `Failed to ${isEdit ? 'update' : 'created'} household. Please try again.`,
            );
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<ContactHouseholdFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>{isEdit ? 'Edit' : 'Create'} Household</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-6'}>
                        <p className={'text-base font-medium'}>Household details</p>
                        <Form.Container>
                            <Form.Label name={path('name')} className={'text-sm font-medium'}>
                                Household Name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot control={control} name={'name'}>
                                <Form.TextInputForm
                                    placeholder={`${contact?.last_name ?? 'Jones'} household`}
                                />
                            </Form.TextInputRoot>
                            <Form.Error name={path('name')} />
                        </Form.Container>
                        <div className="flex flex-col gap-1.5">
                            <Form.Container>
                                <Form.Label
                                    name={path('envelope_name')}
                                    className={'text-sm font-medium'}
                                >
                                    Envelope Name
                                </Form.Label>
                                <Form.TextInputRoot control={control} name={'envelope_name'}>
                                    <Form.TextInputForm
                                        placeholder={`${contact?.last_name ?? 'Jones'} household`}
                                    />
                                </Form.TextInputRoot>
                                <Form.Error name={path('envelope_name')} />
                            </Form.Container>
                        </div>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton>
                                {isEdit ? 'Update' : 'Create'} Household
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { ContactHouseholdFormDrawer };
